// ControlHistoryTab — audit log tab showing chronological history entries.
// Similar to RiskHistoryTab but with control-specific fields
// (lifecycle, scope value, coverage %, exceptions).
//
// NOTE: This requires a backend endpoint GET /controls/{id}/history
// that is not yet implemented (Phase 1 records history but doesn't expose it).
// The tab will show an error state until the endpoint exists.

import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  controlsApi,
  controlLifecycleStateLabels,
  controlLifecycleStateColors,
  type ControlHistoryEntry,
} from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Clock, User, FileText, ArrowRight } from 'lucide-react'

export function ControlHistoryTab() {
  const { slug } = useTenant()
  const { controlId } = useParams({ strict: false }) as { controlId: string }
  const api = useApi()
  const ctrlApi = controlsApi(api)

  const {
    data: history,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.controls.history(slug, controlId),
    queryFn: () => ctrlApi.history(controlId),
  })

  if (isLoading) return <TableSkeleton rows={5} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load audit history"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <p className="text-sm">No history entries yet.</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl p-6">
      <div className="relative">
        <div className="absolute bottom-0 left-4 top-0 w-px bg-border" />
        <div className="space-y-0">
          {history.map((entry, index) => (
            <HistoryEntryRow
              key={entry.id}
              entry={entry}
              isFirst={index === 0}
              previousEntry={index < history.length - 1 ? history[index + 1] : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// --- Change detection ---

interface FieldChange {
  field: string
  from: string | null
  to: string | null
}

function computeChanges(
  entry: ControlHistoryEntry,
  prev: ControlHistoryEntry | undefined,
): FieldChange[] {
  if (!prev) return [{ field: 'Created', from: null, to: null }]

  const changes: FieldChange[] = []

  if (prev.lifecycle_state !== entry.lifecycle_state) {
    changes.push({
      field: 'Lifecycle State',
      from: controlLifecycleStateLabels[prev.lifecycle_state] ?? prev.lifecycle_state,
      to: controlLifecycleStateLabels[entry.lifecycle_state] ?? entry.lifecycle_state,
    })
  }

  if ((prev.scope_value ?? '') !== (entry.scope_value ?? '')) {
    changes.push({
      field: 'Scope Value',
      from: prev.scope_value ?? 'Not set',
      to: entry.scope_value ?? 'Not set',
    })
  }

  if (prev.scope_coverage_percent !== entry.scope_coverage_percent) {
    changes.push({
      field: 'Scope Coverage',
      from:
        prev.scope_coverage_percent != null ? `${prev.scope_coverage_percent}%` : 'Not set',
      to:
        entry.scope_coverage_percent != null
          ? `${entry.scope_coverage_percent}%`
          : 'Not set',
    })
  }

  if ((prev.scope_exceptions ?? '') !== (entry.scope_exceptions ?? '')) {
    changes.push({
      field: 'Scope Exceptions',
      from: prev.scope_exceptions ?? 'Not set',
      to: entry.scope_exceptions ?? 'Not set',
    })
  }

  if (changes.length === 0) {
    changes.push({ field: 'Snapshot recorded', from: null, to: null })
  }

  return changes
}

// --- Individual history entry ---

function HistoryEntryRow({
  entry,
  isFirst,
  previousEntry,
}: {
  entry: ControlHistoryEntry
  isFirst: boolean
  previousEntry?: ControlHistoryEntry
}) {
  const changes = computeChanges(entry, previousEntry)
  const isCreation = !previousEntry

  return (
    <div className="relative pb-6 pl-10">
      <div
        className={cn(
          'absolute left-2.5 h-3 w-3 rounded-full border-2 bg-background',
          isFirst ? 'border-primary' : 'border-muted-foreground/40',
        )}
      />

      <div className="space-y-3 rounded-lg border bg-card p-4">
        {/* Header: who and when */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{entry.changed_by_name}</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <time dateTime={entry.changed_at}>
              {new Date(entry.changed_at).toLocaleString()}
            </time>
          </div>
        </div>

        {/* Change notes */}
        {entry.change_notes && (
          <div className="flex items-start gap-2 text-sm">
            <FileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
            <p className="text-muted-foreground">{entry.change_notes}</p>
          </div>
        )}

        {/* Current state badge */}
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={cn('text-xs', controlLifecycleStateColors[entry.lifecycle_state])}
          >
            {controlLifecycleStateLabels[entry.lifecycle_state]}
          </Badge>
          {isCreation && <span className="text-xs text-muted-foreground">(created)</span>}
        </div>

        {/* Changes summary */}
        {!isCreation && changes.length > 0 && (
          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Changes
            </p>
            {changes.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-36 shrink-0 font-medium text-muted-foreground">{c.field}</span>
                {c.from !== null && c.to !== null ? (
                  <>
                    <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through">
                      {c.from}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">
                      {c.to}
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">{c.field}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
